import { Dayjs } from 'dayjs';
import QueryString from 'qs';

import { API } from '~apis/axios/instance';
import { BillingType, CardProjectionType } from '~apis/card';
import { AccountTransactionSearchParamFieldType } from '~apis/cash-flow-overview/transactions/AccountTransactionSearchParamFieldType';
import { CASH_RECEIPT_TYPE } from '~apis/scrape';
import { TAX_INVOICE_TYPE } from '~apis/tax-invoice';
import { AbstractBankAccount } from '~models/bank';
import { CashFlowType } from '~models/cashFlow';
import { TransactionsMemoModel } from '~models/transactions/memos';
import { Page, ResponseBaseWithData } from '~types/response';

export type MappingLabelType = 'CASH_RECEIPT' | 'TAX_INVOICE' | 'CREDIT_CARD' | 'CHECK_CARD';

interface MappedTo {
  mappingLabel: MappingLabelType;
}

interface MappedToCashReceipt extends MappedTo {
  mappingLabel: 'CASH_RECEIPT';
  cashReceiptId?: string;
  cashReceiptType?: CASH_RECEIPT_TYPE;
  usedDateTime?: string;
  supplierCompanyName?: string;
}

export interface MappedToTaxInvoice extends MappedTo {
  mappingLabel: 'TAX_INVOICE';
  invoiceId: string;
  invoiceType: TAX_INVOICE_TYPE;
  mappedAmount: number;
  reportingDate: string;
  repItems?: string | null;
}

export interface MappedToCreditCard extends MappedTo {
  mappingLabel: 'CREDIT_CARD';
  mappedAmount: number;
  billingItems: BillingItem[];
  adjustments: Adjustment[];
}

export interface BillingItem {
  billingId: number;
  billingType: BillingType;
  billingItemId: number;
  mappingAmount: number;
}

export interface Adjustment {
  categoryId: number;
  businessEntityId?: number;
  groupLabelId?: number;
  companyDetailedCategoryId?: number;
  amount: number;
  paymentDate: string; // ISO 8601 format (e.g., '2025-02-19')
  memo?: string;
}

interface MappedToCheckCard extends MappedTo {
  mappingLabel: 'CHECK_CARD';
  approvalId?: number;
  usedAt?: string;
  memberStoreName?: string;
}

export type MappedToType =
  | MappedToCashReceipt
  | MappedToTaxInvoice
  | MappedToCreditCard
  | MappedToCheckCard;

// todo: 타입 겹치는것 합치기
export interface SplittedTransaction {
  accountId: number;
  businessEntityId: number | null;
  businessEntityName: string | null;
  category1: string;
  category2: string;
  category3: string;
  categoryId: number;
  companyDetailedCategory: string | null;
  companyDetailedCategoryId: number | null;
  categoryLabelKey: string;
  companyId: string;
  currencyCode: string;
  groupLabel: string;
  groupLabelId: number;
  id: number;
  isCustom: boolean;
  isUnclassified: boolean;
  labelId: number;
  labeledBy: LabeledBy;
  mappedTo: MappedToType | null;
  transactionAmount: number;
  transactionAmountKrw: number;
  transactionAt: string;
  transactionId: number;
}

export interface LabeledBankAccountTransaction extends AbstractBankAccount {
  companyId: string;
  accountId: number;
  category?: string;
  categoryId: number | null;
  customLabelId: number | null;
  transactionId: number;
  transactionName: string;
  labelKey: string;
  transactionDescription: string;
  categoryLabelKey?: string;
  currencyCode: string;
  transactionInAmount: number;
  transactionOutAmount: number;
  transactionInKrwAmount: number;
  transactionOutKrwAmount: number;
  diffAmount?: number;
  transactionAfterBalance: number;
  transactionAfterBalanceKrw: number;
  transactionAt: string;
  dailyOrder?: number;
  isUnclassified: boolean;
  businessEntityId: number | null;
  businessEntityLabel?: string;
  memoCount: number;
  hasMapping: boolean;
  mappingLabel?: MappingLabel | null;
  labeledBy?: LabeledBy;
  memo?: TransactionsMemoModel.Memo;
  labelCount: number;
  labelAmount?: number;
  labelKrwAmount?: number;
  labelId: number;
  companyDetailedCategoryId?: number | null;
  mappedObjCount?: number | null;
  classifiedCategoryCount?: number | null;
  businessEntityLabelCount?: number | null;
  groupLabelCount?: number | null;
  mappedAmount?: number;
  isCardPayment?: boolean;
  allLabelIds: number[];
  isSplit?: boolean;
  labels: SplittedTransaction[];
  mappedTo: MappedToType | null;
}

export const enum LabeledBy {
  User = 'USER',
  CompanyRule = 'COMPANY_RULE',
  SystemRule = 'SYSTEM_RULE',
}

interface RecommendFilterBase {
  expression: string;
  count: number;
}

interface RecommendFilterCluster extends RecommendFilterBase {
  type: 'CLUSTER';
  key: string;
}

interface RecommendFilterMappingLabel extends RecommendFilterBase {
  type: 'MAPPING_LABEL';
  key: MappingLabel;
}

export type RecommendFilter = RecommendFilterCluster | RecommendFilterMappingLabel;

export enum MappingLabel {
  TaxInvoice = 'TAX_INVOICE',
  CashReceipt = 'CASH_RECEIPT',
  CheckCard = 'CHECK_CARD',
  CREDIT_CARD = 'CREDIT_CARD',
}

export const MappingLabelTextMap: Record<MappingLabel, string> = {
  [MappingLabel.CheckCard]: '체크카드 결제',
  [MappingLabel.CashReceipt]: '현금영수증 증빙',
  [MappingLabel.TaxInvoice]: '세금계산서 증빙',
  [MappingLabel.CREDIT_CARD]: '카드 청구내역 분류',
};

export const MappingLabelText: Record<MappingLabel, string> = {
  [MappingLabel.CheckCard]: '체크카드',
  [MappingLabel.CashReceipt]: '현금영수증',
  [MappingLabel.TaxInvoice]: '세금계산서',
  [MappingLabel.CREDIT_CARD]: '카드 청구내역',
};

export enum FilterOperation {
  IN = 'IN',
  NOT_IN = 'NOT_IN',
  EMPTY = 'EMPTY',
  NOT_EMPTY = 'NOT_EMPTY',
}

export const getTransactionsByPage = ({
  companyId,
  startDate,
  endDate,
  page,
  categories,
  accounts,
  customLabelIds,
  businessEntityIds,
  detailedCategoryIds,
  categoryLabelKeys,
  searchParam,
  size,
  type,
  sort,
  onlyUnclassified,
  onlyUnmapped,
  clusterId,
  mappingLabel,
  excludeHiddenAccounts,
  accountFilterOp,
  businessEntityIdFilterOp,
  customLabelFilterOp,
  categoryLabelFilterOp,
}: {
  companyId: string;
  startDate: Dayjs;
  endDate: Dayjs;
  page: number;
  categories?: number[];
  accounts?: number[];
  accountFilterOp?: FilterOperation;
  businessEntityIdFilterOp?: FilterOperation;
  customLabelFilterOp?: FilterOperation;
  categoryLabelFilterOp?: FilterOperation;
  customLabelIds?: number[];
  businessEntityIds?: number[];
  detailedCategoryIds?: number[];
  categoryLabelKeys?: string[];
  searchParam?: string;
  size?: number;
  type?: CashFlowType;
  sort?: string[];
  onlyUnclassified?: boolean;
  onlyUnmapped?: boolean;
  clusterId?: number;
  mappingLabel?: MappingLabel;
  excludeHiddenAccounts?: boolean;
}) => {
  const query = QueryString.stringify(
    {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      page,
      categories,
      accounts,
      customLabelIds,
      businessEntityIds,
      detailedCategoryIds,
      categoryLabelKeys,
      searchParam,
      type: type,
      size,
      sort,
      onlyUnclassified,
      onlyUnmapped,
      clusterId,
      mappingLabel,
      excludeHiddenAccounts,
      accountFilterOp,
      businessEntityIdFilterOp,
      customLabelFilterOp,
      categoryLabelFilterOp,
    },
    { arrayFormat: 'repeat' },
  );

  return API.get<
    ResponseBaseWithData<
      Page<LabeledBankAccountTransaction> & {
        totalUnclassifiedCount: number;
        inAmountSumKrw: number;
        outAmountSumKrw: number;
        totalAmountSumKrw: number;
      }
    >
  >(`/api/v2/companies/${companyId}/labelling/transactions?${query}`);
};

export const getFlatTransactionsByPage = ({
  companyId,
  startDate,
  endDate,
  page,
  categories,
  accounts,
  customLabelIds,
  businessEntityIds,
  detailedCategoryIds,
  categoryLabelKeys,
  searchParam,
  searchParamFieldTypes,
  size,
  type,
  sort,
  onlyUnclassified,
  onlyUnmapped,
  clusterId,
  mappingLabel,
  excludeHiddenAccounts,
  accountFilterOp,
  businessEntityIdFilterOp,
  customLabelFilterOp,
  categoryLabelFilterOp,
  mappingLabelTypes,
  mappingLabelTypesFilterOp,
  transactionId,
  cardProjectionType = 'BILLING',
}: {
  companyId: string;
  startDate: Dayjs;
  endDate: Dayjs;
  page: number;
  categories?: number[];
  accounts?: number[];
  accountFilterOp?: FilterOperation;
  businessEntityIdFilterOp?: FilterOperation;
  customLabelFilterOp?: FilterOperation;
  categoryLabelFilterOp?: FilterOperation;
  customLabelIds?: (number | null)[];
  businessEntityIds?: (number | null)[];
  detailedCategoryIds?: number[];
  categoryLabelKeys?: string[];
  searchParam?: string;
  searchParamFieldTypes?: AccountTransactionSearchParamFieldType[];
  size?: number;
  type?: CashFlowType;
  sort?: string[];
  onlyUnclassified?: boolean;
  onlyUnmapped?: boolean;
  clusterId?: number;
  mappingLabel?: MappingLabel;
  excludeHiddenAccounts?: boolean;
  mappingLabelTypes?: MappingLabelType[];
  mappingLabelTypesFilterOp?: FilterOperation;
  transactionId?: number;
  cardProjectionType?: CardProjectionType;
}) => {
  const query = QueryString.stringify(
    {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      page,
      categories,
      accounts,
      customLabelIds,
      businessEntityIds,
      detailedCategoryIds,
      categoryLabelKeys,
      searchParam,
      searchParamFieldTypes,
      type: type,
      size,
      sort,
      onlyUnclassified,
      onlyUnmapped,
      clusterId,
      mappingLabel,
      excludeHiddenAccounts,
      accountFilterOp,
      businessEntityIdFilterOp,
      customLabelFilterOp,
      categoryLabelFilterOp,
      mappingLabelTypes,
      mappingLabelTypesFilterOp,
      transactionId,
      cardProjectionType,
    },
    { arrayFormat: 'repeat' },
  );

  return API.get<
    ResponseBaseWithData<
      Page<LabeledBankAccountTransaction> & {
        totalUnclassifiedCount: number;
        inAmountSumKrw: number;
        outAmountSumKrw: number;
        totalAmountSumKrw: number;
      }
    >
  >(`/api/v2/companies/${companyId}/labelling/transactions/flat?${query}`);
};
